import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  styled,
  Typography,
} from "@mui/material";
import { useProgress } from "context/ProgressProvider";
import { Formik } from "formik";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  AppPrimaryButton,
  AppSecondaryButton,
} from "shared/components/AppComponents";
import { IsPropertyOwnedSchema } from "shared/helpers/Schema";
import AccordionReviewDetails from "./accordion/AccordionReviewDetails";
import { LoanService } from "services/LoanService";
import Utils from "shared/helpers/Utils";
import StripeReviewDetails from "./stripe/StripeReviewDetails";
import { ProfileActions } from "state/profile/Action";
import { PaymentService } from "services/PaymentService";
import { format, parseISO } from "date-fns";
import { useNavigate, useParams } from "react-router-dom";
import Toast from "shared/helpers/Toast";

const ReviewDetails = () => {
  const {
    handleCompleteAndNext,
    navigateToSectionStepHandler,
    currentProfile,
    isBroker,
  } = useProgress();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { step: urlStep } = useParams<{ step: string }>();
  const { handleUpdateUserLoan } = useProgress();

  const currentStep = urlStep ? parseInt(urlStep, 10) : 1;

  const PAGE_LENGTH = 4;
  const PAGE_ID = "verif-review";

  const isValidStep = currentStep >= 1 && currentStep < PAGE_LENGTH;

  useEffect(() => {
    if (!isValidStep) {
      navigate(`/sbl/${PAGE_ID}/1`, { replace: true });
    }
    console.log(currentStep);
  }, [currentStep, isValidStep, navigate]);

  const isPaymentDone =
    currentProfile.paymentDetails &&
    currentProfile.paymentDetails.paymentStatus !== 0
      ? true
      : false;

  const [dialogApproved, setDialogApproved] = useState(false);
  const [dataEligible, setDataEligible] = useState(null);
  const [paymentData, setPaymentData] = useState(null);
  const [loadingForm, setLoadingForm] = useState(
    (currentStep === 2 || currentStep === 3) && isPaymentDone ? false : true,
  );
  const [costData, setCostData] = useState(null);

  const progressInfo = useSelector((state: any) => state.progressState);

  const paymentCalulation = (formik) => {
    PaymentService.costCalculation({
      loanAmount: currentProfile.loanAmount
        ? parseFloat(String(currentProfile.loanAmount).replace(/,/g, ""))
        : null,
      loanTerm: currentProfile.loanTerm,
      loanId: currentProfile.loadId,
    })
      .then((response: any) => {
        handleUpdateLoan();
        let data = response.data.data;
        setCostData(data);
        formik?.setSubmitting(false);
      })
      .catch((error: any) => {
        Utils.handleErrorResponse(error);
      });
  };

  const handleCalculateEligible = (formik) => {
    LoanService.calculateEligibleLoan({ loanId: currentProfile.loadId })
      .then((response: any) => {
        let data = response.data.data;
        setDataEligible(data);
        paymentCalulation(formik);
        setTimeout(() => {
          setDialogApproved(true);
        }, 1000);
      })
      .catch((error: any) => {
        Utils.handleErrorResponse(error);
      })
      .finally(() => {});
  };

  const handleUpdateLoan = () => {
    LoanService.getLoanReview(currentProfile.loadId)
      .then((response) => {
        handleUpdateUserLoan(response);
        setTimeout(() => {
          setLoadingForm(false);
          dispatch(
            ProfileActions.setSblProfile({
              isPayment: false,
            }),
          );
        }, 200);
      })
      .catch((error) => {
        Toast.showErrorMessage(error);
      })
      .finally(() => {
        setTimeout(() => {
          setLoadingForm(false);
          dispatch(
            ProfileActions.setSblProfile({
              isPayment: false,
            }),
          );
        }, 200);
      });
  };

  const handleSubmitForm = (value, formik = null) => {
    const nextStep = currentStep + 1;

    if (isPaymentDone) {
      handleCompleteAndNext();
      return;
    }
    if (currentStep === 1) {
      handleCalculateEligible(formik);

      return;
    }
    if (nextStep < PAGE_LENGTH) {
      navigateToSectionStepHandler(PAGE_ID, nextStep);
    }
  };

  const StyledDialog = styled(Dialog)(({ theme }) => ({
    "&.MuiDialog-root": {
      overflow: "auto !important",
    },
    "& .MuiDialog-container": {
      overflow: "auto !important",
      height: "auto !important",
    },
    "& .MuiPaper-root": {
      maxHeight: "100% !important",
      overflow: "hidden !important",
    },
  }));

  const handleCheckCost = () => {
    if (isPaymentDone) {
      setPaymentData({ ...currentProfile.paymentDetails });
      setTimeout(() => {
        handleUpdateLoan();
      }, 1000);
      return;
    }
    PaymentService.costCalculation({
      loanAmount: currentProfile.loanAmount
        ? parseFloat(String(currentProfile.loanAmount).replace(/,/g, ""))
        : null,
      loanTerm: currentProfile.loanTerm,
      loanId: currentProfile.loadId,
    })
      .then((response: any) => {
        handleUpdateLoan();
        let data = response.data.data;
        setCostData(data);
      })
      .catch((error: any) => {
        Utils.handleErrorResponse(error);
      });
  };

  useEffect(() => {
    setTimeout(() => {
      if (currentStep === 2 || currentStep === 3) {
        if (isPaymentDone) {
          handleCompleteAndNext();
        }
        dispatch(
          ProfileActions.setSblProfile({
            isPayment: true,
          }),
        );
        if (currentStep === 3) {
          const progressData = progressInfo;
          const timeout = setTimeout(() => {
            if (progressData === progressInfo) handleCompleteAndNext();
          }, 4000);

          return () => clearTimeout(timeout);
        }
      }
    }, 200);
  }, [currentStep, progressInfo]);

  useEffect(() => {
    dispatch(
      ProfileActions.setSblProfile({
        isPayment: true,
      }),
    );
    setTimeout(() => {
      handleCheckCost();
    }, 100);
  }, []);
  console.log(paymentData);

  return (
    <>
      {loadingForm && (
        <Box
          sx={{
            textAlign: "center",
            height: "calc(100vh - 50%)",
            alignContent: "center",
          }}
        >
          <CircularProgress />
        </Box>
      )}
      {!loadingForm && currentStep === 1 && (
        <Box>
          <Formik
            enableReinitialize
            validationSchema={IsPropertyOwnedSchema}
            validateOnMount
            onSubmit={(value, formik) => {
              formik.setSubmitting(true);
              handleSubmitForm(value, formik);
            }}
            initialValues={{
              isPropertyOwned: currentProfile.isPropertyOwned,
            }}
          >
            {({
              values,
              setFieldValue,
              handleSubmit,
              errors,
              isSubmitting,
            }) => (
              <form autoComplete="off">
                <Typography variant="h2">Review details</Typography>
                <Typography
                  variant="h3"
                  mt={3}
                  maxWidth={"80%"}
                  fontWeight={400}
                >
                  {isBroker ? (
                    <>
                      To help speed up your customer verification, we’ve
                      pre-filled the details you’ve already provided. Make sure
                      everything is correct before moving forward.
                    </>
                  ) : (
                    <>
                      To help speed up your verification, we’ve pre-filled the
                      details you’ve already provided. Make sure everything is
                      correct before moving forward.
                    </>
                  )}
                </Typography>
                <Box mt={2}>
                  <AccordionReviewDetails />
                </Box>

                <Box mt={4} sx={{ textAlign: "center" }}>
                  <AppPrimaryButton
                    title="Continue"
                    isLoading={isSubmitting}
                    loadingText="Please wait.."
                    onClick={handleSubmit}
                    disabled={Object.keys(errors).length > 0 || isSubmitting}
                  />
                </Box>
              </form>
            )}
          </Formik>
        </Box>
      )}
      {!isPaymentDone && !paymentData && currentStep === 2 && (
        <StripeReviewDetails
          PAGE_ID={PAGE_ID}
          currentStep={currentStep}
          dataEligible={dataEligible}
          setPaymentData={setPaymentData}
          costData={costData}
        />
      )}
      {!isPaymentDone && paymentData && currentStep === 3 && (
        <>
          <Box sx={{ textAlign: "center" }}>
            <img src="/images/payment-success.gif" width={120} height={120} />
            <Typography variant="h2">Payment successful</Typography>
            <Typography variant="h3" fontWeight={400}>
              Your establishment fee has been paid.
            </Typography>

            {/* Box data */}
            <Box
              sx={{
                border: "1px solid #E5E5E5",
                borderRadius: "16px",
                padding: "32px",
              }}
              mt={3}
            >
              <Grid container sx={{ textAlign: "left" }}>
                <Grid
                  size={{ xs: 6, md: 6 }}
                  sx={{ borderRight: "1px solid #E5E5E5" }}
                  component="div"
                >
                  <Box>
                    <Typography
                      variant="h4"
                      fontWeight={400}
                      sx={{ paddingLeft: "16px" }}
                    >
                      Amount
                    </Typography>
                    <Typography
                      variant="h3"
                      fontWeight={400}
                      sx={{ paddingLeft: "16px" }}
                    >
                      ${paymentData?.totalAmount.toLocaleString("en-US")}.00 AUD
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      height: "64px",
                      paddingLeft: "16px",
                      alignContent: "center",
                    }}
                  >
                    <Typography
                      variant="h3"
                      fontWeight={400}
                      sx={{
                        color: "#003AB1",
                        cursor: "pointer",
                      }}
                    >
                      Fee breakdown
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      variant="h4"
                      fontWeight={400}
                      sx={{ paddingLeft: "16px" }}
                    >
                      Payment method
                    </Typography>
                    <Typography
                      variant="h3"
                      fontWeight={400}
                      sx={{ paddingLeft: "16px" }}
                    >
                      Visa •••• 1234
                    </Typography>
                  </Box>
                </Grid>
                <Grid
                  size={{ xs: 6, md: 6 }}
                  sx={{ paddingLeft: "12px" }}
                  component="div"
                >
                  <Box>
                    <Typography
                      variant="h4"
                      fontWeight={400}
                      sx={{ paddingLeft: "16px" }}
                    >
                      Date
                    </Typography>
                    <Typography
                      variant="h3"
                      fontWeight={400}
                      sx={{ paddingLeft: "16px" }}
                    >
                      {format(
                        parseISO(paymentData.paidAt),
                        "dd MMM yyyy, hh:mm a",
                      )}
                    </Typography>
                  </Box>
                  <Box sx={{ height: "64px", paddingLeft: "16px" }}>
                    <Typography variant="h4" fontWeight={400}>
                      Reference
                    </Typography>
                    <Typography variant="h3" fontWeight={400}>
                      {paymentData.paymentIntentId.toUpperCase()}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      variant="h4"
                      fontWeight={400}
                      sx={{ paddingLeft: "16px" }}
                    >
                      Tax invoice number
                    </Typography>
                    <Typography
                      variant="h3"
                      fontWeight={400}
                      sx={{ paddingLeft: "16px" }}
                    >
                      INV-2025-00001
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            <Typography variant="h4" fontWeight={400} mt={3}>
              Redirecting you now...
            </Typography>
          </Box>
        </>
      )}
      {dataEligible && (
        <StyledDialog open={dialogApproved} fullWidth maxWidth="sm">
          <DialogTitle>
            <Box>
              <img src="/images/payment-done.gif" width={120} height={120} />
              <Typography variant="h3" sx={{ fontSize: "22px" }}>
                You’re conditionally approved!
              </Typography>
            </Box>
          </DialogTitle>

          <DialogContent sx={{ overflow: "hidden" }}>
            <Typography
              variant="h3"
              fontWeight={400}
              sx={{
                borderBottom: "1px solid #E5E5E5",
                paddingBottom: "12px",
              }}
            >
              {isBroker ? (
                <>
                  Based on the initial information provided, your customer loan
                  application is conditionally approved, pending completion of a
                  full assessment for unconditional approval.
                </>
              ) : (
                <>
                  Based on the initial information provided, your loan
                  application is conditionally approved, pending completion of a
                  full assessment for unconditional approval.
                </>
              )}
            </Typography>
            <Typography variant="h3" mt={3}>
              Summary
            </Typography>

            {/* Grid 1 */}
            <Grid container spacing={2}>
              <Grid
                size={{ xs: 6, md: 6 }}
                component="div"
                sx={{ textAlign: "left" }}
              >
                <Box>
                  <Typography
                    variant="h3"
                    fontWeight={400}
                    sx={{ color: "#848484" }}
                  >
                    Loan Amount
                  </Typography>
                  <Typography variant="h3" fontWeight={400}>
                    ${dataEligible.loanAmount}
                  </Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 6, md: 6 }} component="div">
                <Box>
                  <Typography
                    variant="h3"
                    fontWeight={400}
                    sx={{ color: "#848484" }}
                  >
                    Loan Term
                  </Typography>
                  <Typography variant="h3" fontWeight={400}>
                    {dataEligible.loanTerm} Months
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            {/* Grid 2 */}
            <Grid container spacing={2} mt={3}>
              <Grid
                size={{ xs: 6, md: 6 }}
                component="div"
                sx={{ textAlign: "left" }}
              >
                <Box>
                  <Typography
                    variant="h3"
                    fontWeight={400}
                    sx={{ color: "#848484" }}
                  >
                    Rate of Interest
                  </Typography>
                  <Typography variant="h3" fontWeight={400}>
                    {dataEligible.rateOfInterest}
                  </Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 6, md: 6 }} component="div">
                <Box>
                  <Typography
                    variant="h3"
                    fontWeight={400}
                    sx={{ color: "#848484" }}
                  >
                    Origination Fee
                  </Typography>
                  <Typography variant="h3" fontWeight={400}>
                    ${dataEligible.originationFee}
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            {false && (
              <Grid container spacing={2} mt={3}>
                <Grid
                  size={{ xs: 6, md: 6 }}
                  component="div"
                  sx={{ textAlign: "left" }}
                >
                  <Box>
                    <Typography
                      variant="h3"
                      fontWeight={400}
                      sx={{ color: "#848484" }}
                    >
                      Management Fee
                    </Typography>
                    <Typography variant="h3" fontWeight={400}>
                      ${dataEligible.managementFee}
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 6, md: 6 }} component="div">
                  <Box>
                    <Typography
                      variant="h3"
                      fontWeight={400}
                      sx={{ color: "#848484" }}
                    >
                      Credit Score
                    </Typography>
                    <Typography variant="h3" fontWeight={400}>
                      {dataEligible.creditScore}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            )}

            <Grid
              container
              spacing={2}
              mt={3}
              sx={{
                borderBottom: "1px solid #E5E5E5",
                paddingBottom: "12px",
              }}
            >
              <Grid
                size={{ xs: 6, md: 6 }}
                component="div"
                sx={{ textAlign: "left" }}
              >
                <Box>
                  <Typography
                    variant="h3"
                    fontWeight={400}
                    sx={{ color: "#848484" }}
                  >
                    Application Fee
                  </Typography>
                  <Typography variant="h3" fontWeight={400}>
                    ${dataEligible.applicationFee}
                  </Typography>
                </Box>
              </Grid>

              {isBroker && (
                <Grid
                  size={{ xs: 6, md: 6 }}
                  component="div"
                  sx={{ textAlign: "left" }}
                >
                  <Box>
                    <Typography
                      variant="h3"
                      fontWeight={400}
                      sx={{ color: "#848484" }}
                    >
                      Broker commission rate
                    </Typography>
                    <Typography variant="h3" fontWeight={400}>
                      {currentProfile.brokerCommissionRate}%
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>

            <Typography variant="h3" mt={3}>
              Important note
            </Typography>

            <Typography
              variant="h4"
              mt={3}
              fontWeight={400}
              lineHeight={"18px"}
            >
              You will now be directed to the payment page to initiate
              processing of your loan application. Please be advised that, in
              the event the loan does not proceed, 50% of the establishment fee
              paid will be refunded. <br />
              <br />
              By clicking Proceed to Payment, you agree to the{" "}
              <a
                href="/terms-of-service"
                target="_blank"
                className="text-underline"
              >
                Terms of service
              </a>{" "}
              &{" "}
              <a
                href="/privacy-policy"
                target="_blank"
                className="text-underline"
              >
                Privacy policy.
              </a>
            </Typography>
          </DialogContent>

          <DialogActions sx={{ gap: "12px" }}>
            <AppSecondaryButton
              title="Cancel"
              className="btn-small ezifin-default-outlined-button"
              onClick={() => {
                setDataEligible(null);
                setDialogApproved(false);
              }}
              fullWidth
            />
            <AppPrimaryButton
              title="Proceed to Payment"
              className="btn-small payment-proceed"
              onClick={() => {
                setDialogApproved(false);
                navigateToSectionStepHandler(PAGE_ID, 2);
              }}
              fullWidth
            />
          </DialogActions>
        </StyledDialog>
      )}
    </>
  );
};

export default ReviewDetails;
